import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    linkedSignal,
    signal,
    untracked,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { SeLayoutConfigComponent } from "./se.layout.config.component";
import { SeLayoutsStore } from "./se.layouts.store";
import { S25Util } from "../../../util/s25-util";
import { Layouts } from "./se.layouts";
import { S25TableModule } from "../../s25-table/s25.table.module";
import { Table } from "../../s25-table/Table";
import { GenericTableButtonComponent } from "../../s25-table/generics/generic.table.button.component";
import { S25Assert } from "../../../util/s25.assert";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25TableComponent } from "../../s25-table/s25.table.component";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { S25LoadingInlineStaticComponent } from "../../s25-loading/s25.loading.inline.static.component";
import { SeLayoutOrgLinkCellComponent } from "./se.layout.org.link.cell.component";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { Item } from "../../../pojo/Item";
import { s25Resource } from "../../../util/s25.resource";
import { OrganizationService } from "../../../services/organization.service";
import { AlertService } from "../../../services/alert.service";

export type LayoutSearchFilter = {
    searchTerm: string;
    organizationId: string;
    page: "" | Layouts.Page;
};

export const DEFAULT_LAYOUT_SEARCH_FILTER: LayoutSearchFilter = {
    searchTerm: "",
    organizationId: "",
    page: "",
};

export type LayoutTableItem = Pick<Layouts.LayoutListItem, "page" | "category"> & {
    id?: number;
    orgId?: number;
};

@TypeManagerDecorator("s25-ng-se-layouts-config")
@Component({
    standalone: true,
    selector: "s25-ng-se-layouts-config",
    host: {
        class: "layouts-config",
        "[class.layouts-config--org]": "isOrgScoped()",
    },
    template: `
        @if (editing()) {
            <h3>{{ Layouts.pageLabel(editing()!.page) }}</h3>
            <s25-ng-se-layout-config
                [page]="editing()!.page"
                [editMode]="editing()!.mode"
                [layoutId]="editing()!.id"
                [nameless]="true"
                [orgId]="editing()!.orgId"
                [category]="editing()!.category"
                [readonly]="readonly()"
                (saved)="onEditorClosed()"
                (cancelled)="onEditorClosed()"
            />
        } @else {
            <div class="layouts-search-container">
                @if (!isOrgScoped()) {
                    <header class="search-header">
                        <h1 class="page-title">Layouts</h1>
                    </header>
                }

                @if (!sourceLayouts()) {
                    <s25-ng-loading-inline-static />
                } @else {
                    @if (!isOrgScoped()) {
                        <section class="layouts-section" data-id="global-layouts">
                            <h2 class="layouts-section-title">Global default</h2>
                            <p class="layouts-section-help">
                                Used when an organization does not have its own layout for that page.
                            </p>
                            <s25-ng-table
                                [caption]="'Global default layouts'"
                                [dataSource]="globalTableSource"
                                [hasSelect]="false"
                                [noResultsMessage]="'No global default layout'"
                                [unlimitedWidth]="true"
                            />
                        </section>
                    }

                    <section class="layouts-section" data-id="org-layouts">
                        @if (!isOrgScoped()) {
                            <h2 class="layouts-section-title">
                                Organization layouts
                                @if (totalResults() !== null) {
                                    <span class="item-count">{{ totalResults() }} total</span>
                                }
                            </h2>

                            <form class="filters-form" (ngSubmit)="runSearch()">
                                <div class="filters-container">
                                    <div class="search-wrapper" role="search">
                                        <label for="se-layouts-search-q" class="visually-hidden">
                                            Search layouts by organization or page
                                        </label>
                                        <s25-ng-icon
                                            [type]="'search'"
                                            [size]="18"
                                            class="search-icon"
                                            [ariaLabel]="false"
                                        />
                                        <input
                                            id="se-layouts-search-q"
                                            class="search-input"
                                            type="search"
                                            name="seLayoutsSearchQuery"
                                            data-id="layouts-search-q"
                                            [ngModel]="searchTermDraft()"
                                            (ngModelChange)="onSearchTermDraftInput($event)"
                                            placeholder="Organization or page"
                                            autocomplete="off"
                                        />
                                        @if (searchTermDraft()) {
                                            <button
                                                class="clear-btn"
                                                type="button"
                                                aria-label="Clear search"
                                                (click)="clearSearchTermDraft()"
                                            >
                                                <s25-ng-icon [type]="'close'" [size]="14" [ariaLabel]="false" />
                                            </button>
                                        }
                                    </div>

                                    <fieldset class="filter-controls">
                                        <legend class="visually-hidden">Filter options</legend>
                                        @if (orgNamesReady() && organizationFilterItems().length) {
                                            <select
                                                id="se-layouts-search-org-filter"
                                                class="filter-select"
                                                name="seLayoutsSearchOrganization"
                                                aria-label="Filter by organization"
                                                [ngModel]="layoutSearchFilter().organizationId"
                                                (ngModelChange)="onOrganizationFilterChange($event)"
                                            >
                                                <option value="">All organizations</option>
                                                @for (
                                                    organization of organizationFilterItems();
                                                    track organization.itemId
                                                ) {
                                                    <option [value]="organization.itemId">
                                                        {{ organization.itemName }}
                                                    </option>
                                                }
                                            </select>
                                        }
                                        <select
                                            id="se-layouts-search-page"
                                            class="filter-select"
                                            name="seLayoutsSearchPage"
                                            aria-label="Filter by page"
                                            [ngModel]="layoutSearchFilter().page"
                                            (ngModelChange)="onPageFilterChange($event)"
                                        >
                                            <option value="">All pages</option>
                                            @for (page of pageOptions; track page) {
                                                <option [value]="page">{{ Layouts.pageLabel(page) }}</option>
                                            }
                                        </select>
                                        <button class="aw-button aw-button--primary search-btn" type="submit">
                                            Search
                                        </button>
                                        @if (hasActiveFilters()) {
                                            <button class="text-btn" type="button" (click)="clearFilters()">
                                                Clear filters
                                            </button>
                                        }
                                    </fieldset>
                                </div>

                                @if (hasActiveFilters()) {
                                    <div class="active-filters" role="group" aria-label="Active filters">
                                        <span class="filter-label">Active filters:</span>
                                        @if (layoutSearchFilter().searchTerm) {
                                            <span class="filter-pill">
                                                "{{ layoutSearchFilter().searchTerm }}"
                                                <button
                                                    type="button"
                                                    (click)="clearAppliedSearchTerm()"
                                                    [attr.aria-label]="
                                                        'Remove search filter: ' + layoutSearchFilter().searchTerm
                                                    "
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        }
                                        @if (organizationFilterLabel()) {
                                            <span class="filter-pill">
                                                {{ organizationFilterLabel() }}
                                                <button
                                                    type="button"
                                                    (click)="onOrganizationFilterChange('')"
                                                    [attr.aria-label]="
                                                        'Remove organization filter: ' + organizationFilterLabel()
                                                    "
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        }
                                        @if (pageFilterLabel()) {
                                            <span class="filter-pill">
                                                {{ pageFilterLabel() }}
                                                <button
                                                    type="button"
                                                    (click)="onPageFilterChange('')"
                                                    [attr.aria-label]="'Remove page filter: ' + pageFilterLabel()"
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        }
                                    </div>
                                }
                            </form>

                            @if (canBulkEdit()) {
                                <details class="bulk-actions-details">
                                    <summary class="bulk-actions-summary">Bulk actions</summary>
                                    <div class="bulk-actions-body">
                                        <span class="bulk-selection">{{ selectedLayoutIds().size }} selected</span>
                                        <div class="bulk-actions">
                                            <button
                                                class="aw-button aw-button--outline"
                                                type="button"
                                                (click)="clearSelection()"
                                                [disabled]="!selectedLayoutIds().size"
                                            >
                                                Clear selection
                                            </button>
                                            <button
                                                class="aw-button aw-button--outline"
                                                type="button"
                                                data-id="layouts-bulk-reset"
                                                (click)="resetSelectedLayouts()"
                                                [disabled]="!selectedLayoutIds().size || bulkResetting()"
                                            >
                                                @if (bulkResetting()) {
                                                    Resetting…
                                                } @else {
                                                    Reset to global default
                                                }
                                            </button>
                                        </div>
                                    </div>
                                </details>
                            }
                        }

                        @if (!isOrgScoped() && !orgNamesReady()) {
                            <s25-ng-loading-inline-static />
                        } @else {
                            <s25-ng-table
                                #layoutsTable
                                [caption]="'Organization layout search results'"
                                [dataSource]="isOrgScoped() ? orgDetailsTableSource : tableSource"
                                [hasSelect]="canBulkEdit()"
                                [hasSelectAll]="canBulkEdit()"
                                [hasTotalRowCount]="!isOrgScoped()"
                                [noResultsMessage]="'No layouts match your search'"
                                [unlimitedWidth]="!isOrgScoped()"
                                (selectedChange)="onLayoutTableSelectionChange($event)"
                            />
                        }
                    </section>
                }
            </div>
        }
    `,
    styles: `
        :host {
            display: block;
            width: 100%;
            max-width: 100%;
            min-width: 0;
            box-sizing: border-box;
        }
        :host ::ng-deep td[col="reset"] button {
            white-space: nowrap;
            height: auto;
            width: auto;
        }
        .layouts-search-container {
            max-width: 1400px;
            margin: 0 auto;
            padding: 0 1rem 2rem;
            box-sizing: border-box;
        }
        :host.layouts-config--org {
            overflow-x: hidden;
        }
        :host.layouts-config--org .layouts-search-container {
            max-width: 100%;
            padding: 0;
            min-width: 0;
        }
        :host.layouts-config--org ::ng-deep s25-ng-table {
            display: block;
            max-width: 100% !important;
            width: 100% !important;
            min-width: 0 !important;
            box-sizing: border-box;
        }
        :host.layouts-config--org ::ng-deep s25-ng-table .tableWrapper {
            max-width: 100%;
            min-width: 0;
            overflow-x: auto;
        }
        :host.layouts-config--org ::ng-deep s25-ng-table .rose-object-table-header,
        :host.layouts-config--org ::ng-deep s25-ng-table table {
            max-width: 100% !important;
            width: 100% !important;
            table-layout: fixed;
        }
        :host.layouts-config--org ::ng-deep s25-ng-table th,
        :host.layouts-config--org ::ng-deep s25-ng-table td {
            min-width: 0 !important;
        }
        :host.layouts-config--org ::ng-deep s25-ng-table [col="edit"] {
            width: 9rem;
        }
        :host.layouts-config--org ::ng-deep s25-ng-table [col="reset"] {
            width: 14rem;
        }
        :host.layouts-config--org ::ng-deep s25-ng-table td > * {
            min-width: 0 !important;
            max-width: 100% !important;
            width: auto !important;
        }
        .search-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1.5rem;
            padding-bottom: 1rem;
            border-bottom: 1px solid var(--color--border, #e5e7eb);
        }
        .page-title {
            margin: 0;
            font-size: 1.5rem;
        }
        .item-count {
            margin-left: 0.75rem;
            color: var(--color--text-muted, #6b7280);
            font-weight: 400;
        }
        .item-count--block {
            display: block;
            margin: 0 0 1rem;
            margin-left: 0;
        }
        .filters-form {
            margin-bottom: 1.5rem;
        }
        .filters-container {
            display: flex;
            gap: 1rem;
            flex-wrap: wrap;
            align-items: flex-start;
        }
        .visually-hidden {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
        }
        .search-wrapper {
            flex: 1;
            min-width: 240px;
            position: relative;
        }
        .search-icon {
            position: absolute;
            left: 0.75rem;
            top: 50%;
            transform: translateY(-50%);
            color: var(--color--text-muted, #6b7280);
            pointer-events: none;
        }
        .search-input {
            width: 100%;
            padding: 0.5rem 2rem 0.5rem 2.5rem;
            border: 1px solid var(--color--border-bold, #b3b3b3);
            border-radius: 6px;
            font-size: 0.875rem;
            transition: border-color var(--transition-medium, 200ms) ease;
            background: var(--color--form-bg, white);
            color: var(--color--text, #333);
        }
        .search-input:focus {
            outline: none;
            border-color: var(--color--border-bold-hover, #666);
            box-shadow: var(--shadow--form-focus, 0 0 0 2px #fafaf9, 0 0 0 4px #2573a7);
        }
        .search-input::-webkit-search-cancel-button {
            -webkit-appearance: none;
            appearance: none;
        }
        .clear-btn {
            position: absolute;
            right: 0.5rem;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            color: var(--color--text-muted, #666);
            cursor: pointer;
            padding: 0.25rem;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: var(--border-radius, 3px);
        }
        .filter-controls {
            display: flex;
            gap: 0.75rem;
            align-items: flex-start;
            border: none;
            padding: 0;
            margin: 0;
        }
        .filter-select {
            padding: 0.5rem 2rem 0.5rem 0.75rem;
            border: 1px solid var(--color--border-bold, #b3b3b3);
            border-radius: 6px;
            font-size: 0.875rem;
            cursor: pointer;
            appearance: none;
            background: var(--color--form-bg, white)
                url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")
                no-repeat right 0.5rem center;
            background-size: 16px;
            min-width: 140px;
            color: var(--color--text, #333);
        }
        .filter-select:focus {
            outline: none;
            border-color: var(--color--border-bold-hover, #666);
            box-shadow: var(--shadow--form-focus, 0 0 0 2px #fafaf9, 0 0 0 4px #2573a7);
        }
        .text-btn {
            background: none;
            border: none;
            color: var(--color--text-muted, #666);
            font-size: 0.875rem;
            cursor: pointer;
            padding: 0.5rem 0.75rem;
            border-radius: 6px;
            white-space: nowrap;
        }
        .text-btn:hover {
            color: var(--color--text, #333);
            background: var(--color--surface-2-bg, #f0f0f0);
        }
        .search-btn {
            white-space: nowrap;
            align-self: flex-start;
        }
        .active-filters {
            display: flex;
            gap: 0.5rem;
            align-items: center;
            margin-top: 1rem;
            font-size: 0.875rem;
            flex-wrap: wrap;
        }
        .active-filters .filter-label {
            color: var(--color--text-muted, #666);
        }
        .filter-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.375rem;
            padding: 0.25rem 0.625rem;
            background: var(--color--surface-2-bg, #f0f0f0);
            border-radius: var(--border-radius, 3px);
            font-size: 0.8125rem;
            color: var(--color--text, #333);
        }
        .filter-pill button {
            background: none;
            border: none;
            color: var(--color--text-muted, #666);
            cursor: pointer;
            padding: 0.125rem;
            font-size: 1.125rem;
            line-height: 1;
        }
        @media (max-width: 768px) {
            .filters-container {
                flex-direction: column;
            }
            .filter-controls {
                flex-direction: column;
                align-items: stretch;
                width: 100%;
            }
            .filter-select {
                width: 100%;
            }
        }
        .layouts-section {
            margin-bottom: 2rem;
        }
        .layouts-section-title {
            margin: 0 0 0.35rem;
            font-size: 1.125rem;
        }
        .layouts-section-help {
            margin: 0 0 0.75rem;
            color: var(--color--text-muted, #6b7280);
            font-size: 0.875rem;
        }
        .bulk-actions-details {
            margin-bottom: 1rem;
            border: 1px solid var(--color--border);
            border-radius: 4px;
            background: var(--color--surface-2-bg);
        }
        .bulk-actions-summary {
            padding: 0.65rem 1rem;
            font-weight: 600;
            cursor: pointer;
            list-style: none;
        }
        .bulk-actions-summary::-webkit-details-marker {
            display: none;
        }
        .bulk-actions-body {
            padding: 0 1rem 1rem;
            border-top: 1px solid var(--color--border);
        }
        .bulk-selection {
            display: block;
            font-weight: 600;
            margin: 0.65rem 0 0.5rem;
        }
        .bulk-actions {
            margin-top: 0.75rem;
            display: flex;
            gap: 0.5rem;
            flex-wrap: wrap;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [FormsModule, SeLayoutConfigComponent, S25TableModule, S25IconComponent, S25LoadingInlineStaticComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SeLayoutsConfigComponent {
    readonly category = input.required<Layouts.Category>();
    readonly orgId = input<number>();
    readonly readonly = input(false);

    protected readonly Layouts = Layouts;
    protected readonly pageOptions = S25Util.objectKeys(Layouts.pages);
    readonly store = inject(SeLayoutsStore);

    readonly layoutsTable = viewChild<S25TableComponent>("layoutsTable");

    readonly isOrgScoped = computed(() => this.category() === "specificOrg");

    readonly sourceLayouts = computed(() => {
        if (this.isOrgScoped()) {
            const orgId = this.orgId();
            S25Assert(orgId, "orgId is required for category 'specificOrg'");
            return this.store.getOrgLayouts(orgId);
        }
        return this.store.list();
    });

    readonly layoutSearchFilter = signal<LayoutSearchFilter>({ ...DEFAULT_LAYOUT_SEARCH_FILTER });
    readonly searchTermDraft = linkedSignal(() => this.layoutSearchFilter().searchTerm);

    readonly hasActiveFilters = computed(() =>
        SeLayoutsConfigComponent.hasSearchCriteria(this.layoutSearchFilter(), this.isOrgScoped()),
    );

    readonly pageFilterLabel = computed(() => {
        const page = this.layoutSearchFilter().page;
        return page ? Layouts.pageLabel(page) : "";
    });

    readonly organizationFilterLabel = computed(() => {
        const organizationId = this.layoutSearchFilter().organizationId;
        if (!organizationId) return "";
        return (
            this.organizationFilterItems().find((organization) => String(organization.itemId) === organizationId)
                ?.itemName ?? ""
        );
    });

    readonly orgIdsKey = computed(() => {
        const layouts = this.sourceLayouts();
        if (!layouts) return "";
        const orgIds = [
            ...new Set(
                layouts
                    .map((layout) => layout.orgId)
                    .filter((orgId) => Layouts.hasOrgId(orgId))
                    .map((orgId) => Number(orgId)),
            ),
        ].sort((left, right) => left - right);
        return orgIds.join(",");
    });

    readonly orgNames = s25Resource({
        if: () => !this.isOrgScoped() && !!this.orgIdsKey(),
        params: () => this.orgIdsKey(),
        loader: ({ params: orgIdsKey }) =>
            OrganizationService.getOrganizationsMinimal(orgIdsKey.split(",").map((orgId) => Number(orgId))),
    });

    readonly orgNamesById = computed((): Map<number, string> => {
        const organizations = this.orgNames.value();
        return new Map(
            (organizations ?? []).map((organization) => [
                organization.itemId,
                organization.itemName ?? `Organization ${organization.itemId}`,
            ]),
        );
    });

    readonly orgNamesReady = computed(() => {
        if (this.isOrgScoped() || !this.orgIdsKey()) return true;
        return this.orgNames.resource.hasValue() || !!this.orgNames.resource.error();
    });

    /** Organizations that already have a custom layout — used as the org filter list. */
    readonly organizationFilterItems = computed((): DropDownItem[] => {
        const orgIdsKey = this.orgIdsKey();
        if (!orgIdsKey) return [];
        const orgNamesById = this.orgNamesById();
        return orgIdsKey
            .split(",")
            .map((orgIdText) => {
                const itemId = Number(orgIdText);
                return {
                    itemId,
                    itemTypeId: Item.Ids.Organization,
                    itemName: orgNamesById.get(itemId) || `Organization ${itemId}`,
                };
            })
            .sort((left, right) => left.itemName.localeCompare(right.itemName));
    });

    readonly globalLayouts = computed((): LayoutTableItem[] | undefined => {
        if (this.isOrgScoped()) return [];
        const defaults = this.store.defaultLayouts();
        if (!defaults) return undefined;
        return [...defaults, ...this.missingGlobalPlaceholders(defaults)];
    });

    readonly orgLayouts = computed((): LayoutTableItem[] | undefined => {
        const layouts = this.sourceLayouts();
        if (!layouts) return undefined;
        if (this.isOrgScoped()) {
            return [...layouts, ...this.missingOrgPlaceholders(layouts)];
        }
        return layouts.filter((layout) => Layouts.isPageOverride(layout));
    });

    readonly filteredLayouts = computed(() => {
        const layouts = this.orgLayouts();
        if (!layouts) return undefined;
        const filter = this.layoutSearchFilter();
        const orgNamesById = this.orgNamesById();
        return layouts
            .filter((layout) => this.layoutMatchesFilter(layout, filter, orgNamesById))
            .sort((left, right) => this.compareLayoutsByOrganization(left, right, orgNamesById));
    });

    readonly totalResults = computed(() => this.filteredLayouts()?.length ?? null);

    readonly canBulkEdit = computed(() => !this.readonly() && !this.isOrgScoped());
    readonly bulkResetting = signal(false);
    readonly selectedLayoutIds = signal(new Set<number>());

    readonly editing = signal<
        | { page: Layouts.Page; mode: "edit" | "create"; id?: number; category: Layouts.Category; orgId?: number }
        | undefined
    >(undefined);

    readonly onEdit = (row: Table.Row) => {
        if (this.isOrgScoped() && this.readonly()) return;
        const layout = <LayoutTableItem>row.data.layout;
        const mode = !layout.id ? "create" : "edit";
        this.editing.set({
            page: layout.page,
            mode,
            id: layout.id,
            category: layout.category,
            orgId: layout.orgId ?? this.orgId(),
        });
    };

    readonly onReset = (row: Table.Row) => {
        const layout = <LayoutTableItem>row.data.layout;
        if (!layout.id || !Layouts.isPageOverride(layout)) return;
        return this.resetLayouts([layout.id]);
    };

    readonly editButtonColumn = GenericTableButtonComponent.Column("Edit", this.onEdit);

    readonly resetButtonColumn = GenericTableButtonComponent.Column(
        "Reset to default",
        this.onReset,
        "outline",
        "min-content",
        false,
        "Reset",
    );

    readonly tableSource: Table.DataSource = {
        type: "unpaginated",
        dataSource: () => this.getRows(),
        columns: [
            { id: "organization", header: "Organization", export: true },
            { id: "page", header: "Page", export: true },
            this.editButtonColumn,
            { ...this.resetButtonColumn, id: "reset" },
        ],
    };

    readonly orgDetailsTableSource: Table.DataSource = {
        type: "unpaginated",
        dataSource: () => this.getRows(),
        columns: [
            { id: "page", header: "Page", export: true },
            { ...this.editButtonColumn, width: 144 },
            { ...this.resetButtonColumn, id: "reset", width: 200 },
        ],
    };

    readonly globalTableSource: Table.DataSource = {
        type: "unpaginated",
        dataSource: () => this.getGlobalRows(),
        columns: [
            { id: "page", header: "Page", export: true },
            GenericTableButtonComponent.Column("Edit", this.onEdit),
        ],
    };

    constructor() {
        effect(() => {
            this.layoutSearchFilter();
            this.readonly();
            untracked(() => this.refreshLayoutsTable(true));
        });
    }

    private static hasSearchCriteria(filter: LayoutSearchFilter, orgScoped: boolean): boolean {
        return !!filter.searchTerm.trim() || (!orgScoped && !!filter.organizationId) || !!filter.page;
    }

    layoutMatchesFilter(
        layout: LayoutTableItem,
        filter: LayoutSearchFilter,
        orgNamesById: Map<number, string>,
    ): boolean {
        if (filter.page && layout.page !== filter.page) return false;
        if (!this.isOrgScoped() && filter.organizationId) {
            if (!Layouts.hasOrgId(layout.orgId) || Number(layout.orgId) !== Number(filter.organizationId)) {
                return false;
            }
        }
        const searchTerm = filter.searchTerm.trim().toLowerCase();
        if (!searchTerm) return true;
        const haystack = [
            layout.id != null ? String(layout.id) : "",
            layout.orgId != null ? String(layout.orgId) : "",
            this.organizationLabel(layout, orgNamesById),
            Layouts.pageLabel(layout.page),
            layout.page,
        ]
            .filter((value): value is string => !!value)
            .join(" ")
            .toLowerCase();
        return haystack.includes(searchTerm);
    }

    private compareLayoutsByOrganization(
        left: LayoutTableItem,
        right: LayoutTableItem,
        orgNamesById: Map<number, string>,
    ): number {
        const organizationCompare = this.organizationLabel(left, orgNamesById).localeCompare(
            this.organizationLabel(right, orgNamesById),
            undefined,
            { sensitivity: "base" },
        );
        if (organizationCompare !== 0) return organizationCompare;
        return Layouts.pageLabel(left.page).localeCompare(Layouts.pageLabel(right.page), undefined, {
            sensitivity: "base",
        });
    }

    organizationLabel(layout: LayoutTableItem, orgNamesById: Map<number, string>): string {
        if (Layouts.hasOrgId(layout.orgId)) {
            const organizationId = Number(layout.orgId);
            return orgNamesById.get(organizationId) || `Organization ${organizationId}`;
        }
        switch (layout.category) {
            case "globalDefaults":
                return "Global";
            case "specificOrg":
                return "Organization";
            case "unknown":
                return "Unknown";
            default:
                return S25Assert.never(layout.category, "Unhandled layout category");
        }
    }

    async getRows(): Promise<Table.DataSourceResponse> {
        return {
            rows: this.buildOrgRows(this.filteredLayouts() ?? [], this.canBulkEdit()),
        };
    }

    async getGlobalRows(): Promise<Table.DataSourceResponse> {
        return {
            rows: this.buildGlobalRows(this.globalLayouts() ?? []),
        };
    }

    private buildOrgRows(layouts: LayoutTableItem[], allowBulkSelect: boolean): Table.Row[] {
        const orgNamesById = this.orgNamesById();
        return layouts.map((layout) => {
            const pageLabel = Layouts.pageLabel(layout.page);
            const organizationLabel = this.organizationLabel(layout, orgNamesById);
            return {
                id: layout.id ?? `new-${layout.page}`,
                name: `${organizationLabel} ${pageLabel}`,
                selectable: allowBulkSelect && !!layout.id,
                data: { layout },
                cells: {
                    organization: this.organizationCell(layout, organizationLabel),
                    page: { text: pageLabel },
                    edit: this.editCell(),
                    reset: this.resetCell(layout),
                },
            };
        });
    }

    private actionsLocked(): boolean {
        return this.isOrgScoped() && this.readonly();
    }

    private editCell(): Table.Cell {
        const disabled = this.actionsLocked();
        return {
            inputs: {
                ...this.editButtonColumn.cells?.inputs,
                disabled,
                disabledReason: disabled ? "Turn on Edit Mode to make changes." : undefined,
            },
            textValue: "Edit",
        };
    }

    private resetCell(layout: LayoutTableItem): Table.Cell {
        const hasCustomLayout = !!(layout.id && Layouts.isPageOverride(layout));
        if (!hasCustomLayout) {
            return {
                inputs: {
                    ...this.resetButtonColumn.cells?.inputs,
                    disabled: true,
                    disabledReason: "Using global default",
                },
                textValue: "Reset to default",
            };
        }
        const actionsLocked = this.actionsLocked();
        return {
            inputs: {
                ...this.resetButtonColumn.cells?.inputs,
                disabled: actionsLocked,
                disabledReason: actionsLocked ? "Turn on Edit Mode to make changes." : undefined,
            },
            textValue: "Reset to default",
        };
    }

    private organizationCell(layout: LayoutTableItem, organizationLabel: string): Table.Cell {
        if (!Layouts.hasOrgId(layout.orgId)) {
            return { text: organizationLabel };
        }
        return {
            component: SeLayoutOrgLinkCellComponent,
            inputs: {
                itemId: Number(layout.orgId),
                itemName: organizationLabel,
            },
            textValue: organizationLabel,
        };
    }

    private buildGlobalRows(layouts: LayoutTableItem[]): Table.Row[] {
        return layouts.map((layout) => {
            const pageLabel = Layouts.pageLabel(layout.page);
            return {
                id: layout.id ?? `new-global-${layout.page}`,
                name: pageLabel,
                selectable: false,
                data: { layout },
                cells: {
                    page: { text: pageLabel },
                },
            };
        });
    }

    protected runSearch(): void {
        this.commitSearchTerm();
        this.clearSelection();
        this.refreshLayoutsTable(true);
    }

    protected onSearchTermDraftInput(value: string): void {
        this.searchTermDraft.set(value);
    }

    protected clearSearchTermDraft(): void {
        this.searchTermDraft.set("");
    }

    protected clearAppliedSearchTerm(): void {
        this.layoutSearchFilter.update((filter) => ({ ...filter, searchTerm: "" }));
        this.searchTermDraft.set("");
    }

    private commitSearchTerm(): void {
        const searchTerm = this.searchTermDraft();
        if (this.layoutSearchFilter().searchTerm === searchTerm) return;
        this.layoutSearchFilter.update((filter) => ({ ...filter, searchTerm }));
    }

    protected onOrganizationFilterChange(organizationId: string): void {
        this.layoutSearchFilter.update((filter) => ({ ...filter, organizationId }));
    }

    protected onPageFilterChange(page: "" | Layouts.Page): void {
        this.layoutSearchFilter.update((filter) => ({ ...filter, page }));
    }

    clearFilters(): void {
        this.layoutSearchFilter.set({ ...DEFAULT_LAYOUT_SEARCH_FILTER });
        this.searchTermDraft.set("");
        this.clearSelection();
    }

    onLayoutTableSelectionChange(selected: Set<Table.Row["id"]>): void {
        const nextSet = new Set<number>();
        for (const rowId of selected) {
            const numeric = typeof rowId === "number" ? rowId : Number.parseInt(String(rowId), 10);
            if (Number.isFinite(numeric)) nextSet.add(numeric);
        }
        this.selectedLayoutIds.set(nextSet);
    }

    clearSelection(): void {
        this.selectedLayoutIds.set(new Set());
        this.layoutsTable()?.clearSelections();
    }

    onEditorClosed(): void {
        this.editing.set(undefined);
    }

    async resetSelectedLayouts(): Promise<void> {
        return this.resetLayouts([...this.selectedLayoutIds()]);
    }

    async resetLayouts(layoutIds: number[]): Promise<void> {
        if (!layoutIds.length || this.readonly()) return;
        const overrides = this.resettableOverrides(layoutIds);
        if (!overrides.length) return;
        const confirmed = await AlertService.confirm(Layouts.resetToDefaultConfirmMessage(overrides.length));
        if (!confirmed) return;
        this.bulkResetting.set(true);
        try {
            for (const layout of overrides) {
                await this.store.deleteLayout(layout.id);
            }
            await AlertService.alert(
                overrides.length === 1 ? "Layout reset to the global default." : "Layouts reset to the global default.",
            );
            this.clearSelection();
            this.refreshLayoutsTable(true);
        } catch (caughtError) {
            const message = caughtError instanceof Error ? caughtError.message : String(caughtError);
            await AlertService.alert(`Reset failed: ${message}`);
        } finally {
            this.bulkResetting.set(false);
        }
    }

    /**
     * Organization (and unknown) overrides only, in selection order. Each is removed so that page
     * falls back to its global default ({@link SeLayoutsStore.getDefaultLayoutId} / {@link SeLayoutsStore.getOrgLayoutForPage}).
     */
    resettableOverrides(layoutIds: number[]): Layouts.LayoutListItem[] {
        const layoutsById = new Map(
            (this.sourceLayouts() ?? []).filter((layout) => layout.id != null).map((layout) => [layout.id, layout]),
        );
        const overrides: Layouts.LayoutListItem[] = [];
        for (const layoutId of layoutIds) {
            const layout = layoutsById.get(layoutId);
            if (!layout || !Layouts.isPageOverride(layout)) continue;
            overrides.push(layout);
        }
        return overrides;
    }

    private missingOrgPlaceholders(layouts: Layouts.LayoutListItem[]): LayoutTableItem[] {
        const allPages = S25Util.objectKeys(Layouts.pages);
        const existingPages = new Set(layouts.map((layout) => layout.page));
        return allPages
            .filter((page) => !existingPages.has(page))
            .map((page) => ({
                page,
                category: this.category(),
                orgId: this.orgId(),
            }));
    }

    private missingGlobalPlaceholders(defaults: Layouts.LayoutListItem[]): LayoutTableItem[] {
        const allPages = S25Util.objectKeys(Layouts.pages);
        const existingDefaultPages = new Set(defaults.map((layout) => layout.page));
        return allPages
            .filter((page) => !existingDefaultPages.has(page))
            .map((page) => ({
                page,
                category: "globalDefaults",
            }));
    }

    private refreshLayoutsTable(resetPagination: boolean): void {
        const table = this.layoutsTable();
        if (!table || table.loading()) return;
        table.filterValue = "";
        void table.refresh(true, resetPagination, false);
    }
}
